// ============================================================================
// HoneyPacker — compliant bulk SMS sender (SignalHouse).
// "Send from" is a multi-select grouped by use case. COMPLIANCE GUARDRAIL:
// only ONE use-case type may be selected per send (marketing and account-
// notification numbers are registered under different A2P use cases, so mixing
// them in one send is blocked). The send's use case is DERIVED from the picked
// numbers — no separate dropdown that could contradict the selection.
//
// Feature parity with the legacy Giggle Send page:
//   • Wallet balance + ≈message credit (GET /api/wallet), with Refresh.
//   • CSV + XLSX/XLS upload (parsed client-side with SheetJS) → clickable
//     personalization-token chips + per-row variables.
//   • Prev/Next preview that pages through the uploaded rows, personalizing the
//     pink bubble with THAT row's data (live char/segment count).
//   • Review & Send controls: delay-between-messages, daily cap, skip-T-Mobile.
//   • Real send is a CLIENT-DRIVEN LOOP over /api/send-one (one message per
//     request) so the browser owns delay / cap / skip / live progress / Stop.
//   • Export results CSV after a run.
//   • The batch dry-run "Preview & compliance check" (/api/send) is unchanged.
//   • Single-number "Send test" is unchanged.
// (Scheduling / send-later is intentionally NOT here — it needs a cron trigger.)
// ============================================================================
import * as XLSX from 'xlsx'
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { fmtPhone, normPhone } from '../lib/format.js'
import { saveCheckpoint, loadCheckpoint, clearCheckpoint } from '../lib/checkpoint.js'

// GSM-ish segment estimate: 160 chars per segment, 153 when concatenated.
function segments(text) {
  const n = (text || '').length
  if (n === 0) return 0
  return n <= 160 ? 1 : Math.ceil(n / 153)
}
function personalizePreview(body, vars) {
  // Case-insensitive token match so {{FirstName}} resolves against a "firstname"
  // header too (matches the legacy renderTemplate behaviour).
  return String(body || '').replace(/\{\{\s*([\w.\- ]+?)\s*\}\}/g, (_, k) => {
    if (!vars) return ''
    if (vars[k] != null) return String(vars[k])
    const hit = Object.keys(vars).find((h) => h.toLowerCase() === String(k).toLowerCase())
    return hit != null && vars[hit] != null ? String(vars[hit]) : ''
  })
}
// Map a number's label to the compliance use case the send is treated as.
function useCaseFor(label) {
  return label === 'Account Notification' ? 'account_notification' : 'marketing'
}
// Column heuristics (mirror the legacy guessers).
function guessPhoneCol(cols) {
  const pats = [/phone/i, /mobile/i, /cell/i, /number/i, /^to$/i, /recipient/i, /tel/i]
  for (const p of pats) { const c = cols.find((x) => p.test(x)); if (c) return c }
  return cols[0]
}
function guessCarrierCol(cols) {
  return (cols || []).find((c) => /carrier|network|mno|sms.?provider/i.test(c)) || null
}
// Identity column = what Customer.io matches on (email preferred, else cio_id / id).
function guessIdentityCol(cols) {
  const pats = [/^e-?mail$/i, /e-?mail/i, /^cio_?id$/i, /cio/i, /customer.?id/i, /\bid\b/i, /identifier/i]
  for (const p of pats) { const c = (cols || []).find((x) => p.test(x)); if (c) return c }
  return null
}
// Pull a usable identity (email or id) out of a raw string token.
function looksLikeEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim()) }
function isTmobile(v) {
  return /t-?mobile|metro\s*(by|pcs)|\bmint\b|ultra\s*mobile|assurance/i.test(String(v || ''))
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

// <input type="datetime-local"> yields a NAIVE wall-clock ("2026-08-05T14:30")
// with no zone. We interpret that wall-clock as America/New_York (ET) and return
// the corresponding UTC ms epoch — correct across EST/EDT and DST boundaries.
function etWallClockToMs(localStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(localStr || ''))
  if (!m) return NaN
  const [, Y, Mo, D, H, Mi] = m
  // How far ahead of UTC is ET at the instant `utcMs`? (local - utc, in ms)
  const etOffsetMs = (utcMs) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, hourCycle: 'h23',
    }).formatToParts(new Date(utcMs))
    const p = {}
    for (const { type, value } of parts) p[type] = value
    const hh = p.hour === '24' ? 0 : Number(p.hour)
    const asIfUtc = Date.UTC(+p.year, +p.month - 1, +p.day, hh, +p.minute, +p.second)
    return asIfUtc - utcMs
  }
  // Treat the wall-clock as if it were UTC, then subtract ET's offset. One refine
  // pass pins the result on the correct side of a DST transition.
  const guess = Date.UTC(+Y, +Mo - 1, +D, +H, +Mi, 0)
  let utc = guess - etOffsetMs(guess)
  utc = guess - etOffsetMs(utc)
  return utc
}

// ms epoch -> "YYYY-MM-DD HH:MM" in ET (default value for the Reschedule prompt).
function msToEtWallClock(ms) {
  const p = {}
  for (const { type, value } of new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23',
  }).formatToParts(new Date(Number(ms)))) p[type] = value
  return `${p.year}-${p.month}-${p.day} ${p.hour === '24' ? '00' : p.hour}:${p.minute}`
}

// Render a ms epoch as an ET wall-clock string for the "Upcoming sends" table.
function fmtEt(ms) {
  if (!Number.isFinite(Number(ms))) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(Number(ms))) + ' ET'
}

export function renderPacker(el) {
  el.innerHTML = `
    <div class="grid" style="gap:18px;max-width:900px">
      <!-- 1) USE CASE + SEND FROM (use-case-first, compliance-driven) -->
      <div class="card pad">
        <div class="mod-h">1 · Use case &amp; send-from</div>
        <div class="mod-sub">Pick the <b>use case</b> first — it drives A2P compliance and the send gate — then choose from the numbers eligible for it. Toll-free numbers can carry either.</div>
        <div class="toolbar" style="margin:4px 0 12px">
          <button class="btn" id="pUcMarketing" data-uc="marketing">Marketing</button>
          <button class="btn ghost" id="pUcNotif" data-uc="account_notification">Account notification</button>
        </div>
        <div id="pNumGroups"><div class="empty">Loading numbers…</div></div>
        <div class="toolbar" style="margin-top:10px">
          <span class="pill" id="pUseBadge">No numbers selected</span>
          <span class="grow"></span>
          <button class="btn ghost" id="pClearNums" style="padding:5px 12px">Clear</button>
        </div>
        <div class="toolbar" style="margin-top:10px;border-top:1px solid var(--line-soft);padding-top:10px">
          <span class="pill" id="pWallet">Wallet balance …</span>
          <a href="#" id="pWalletRefresh" style="font-size:12px">↻ Refresh</a>
          <span class="grow"></span>
        </div>
        <div class="mod-sub" style="margin:6px 0 0;font-size:12px">🔒 The SignalHouse key stays on the server — never in your browser.</div>
      </div>

      <!-- 2) CAMPAIGN -->
      <div class="card pad">
        <div class="mod-h">2 · Campaign</div>
        <div class="mod-sub">Required — this send is saved to Campaigns with full reporting.</div>
        <input class="h-input" id="pCampaign" placeholder="e.g. Plaid reminder — August" />
      </div>

      <!-- 3) RECIPIENTS -->
      <div class="card pad">
        <div class="mod-h">3 · Recipients</div>
        <div class="mod-sub">Import a <b>.csv / .xlsx</b> with a phone column <b>and</b> an <b>email or CIO ID</b> column (required — matches sends to Customer.io for conversion tracking). Or paste <code>phone, email</code> per line.</div>
        <textarea class="h-input" id="pRecipients" style="min-height:120px" placeholder="18885551234, jane@example.com
18885550000, john@example.com"></textarea>
        <div class="toolbar" style="margin:10px 0 0">
          <input type="file" id="pFile" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" style="font-size:13px" />
          <span class="grow"></span>
          <span class="mod-sub" id="pCount" style="margin:0"></span>
        </div>
        <div id="pFileInfo" style="display:none;margin-top:12px;border-top:1px solid var(--line-soft);padding-top:12px">
          <div class="form-grid">
            <div class="field">
              <label style="font-size:12px;font-weight:700;color:var(--muted)">Phone number column</label>
              <select class="h-input" id="pPhoneCol"></select>
            </div>
            <div class="field">
              <label style="font-size:12px;font-weight:700;color:var(--muted)">Email / CIO ID column <span style="color:var(--pink)">*required</span></label>
              <select class="h-input" id="pIdCol"></select>
            </div>
            <div class="field">
              <label style="font-size:12px;font-weight:700;color:var(--muted)">File</label>
              <div class="toolbar" style="margin:6px 0 0"><span class="pill" id="pFileName"></span><button class="btn ghost" id="pClearFile" style="padding:4px 10px">Clear file</button></div>
            </div>
          </div>
          <div class="stat-row" style="margin-top:10px">
            <div class="stat"><div class="k">Rows</div><div class="v" id="pStatRows">0</div></div>
            <div class="stat"><div class="k">Valid</div><div class="v" id="pStatValid" style="color:var(--ok)">0</div></div>
            <div class="stat"><div class="k">Invalid</div><div class="v" id="pStatInvalid">0</div></div>
            <div class="stat"><div class="k">Duplicates</div><div class="v" id="pStatDupe">0</div></div>
            <div class="stat"><div class="k">Missing ID</div><div class="v" id="pStatNoId" style="color:var(--pink)">0</div></div>
            <div class="stat"><div class="k">T-Mobile skipped</div><div class="v" id="pStatTmo">0</div></div>
          </div>
        </div>
      </div>

      <!-- 4) MESSAGE -->
      <div class="card pad">
        <div class="mod-h">4 · Message</div>
        <div class="mod-sub">Use <code>{{first_name}}</code> tokens for personalization. Always include an opt-out.</div>
        <textarea class="h-input" id="pBody" style="min-height:100px" placeholder="Hi {{first_name}}, … Reply STOP to opt out."></textarea>
        <div class="toolbar" style="margin:10px 0 0">
          <span class="mod-sub" style="margin:0">Link shortener</span>
          <select class="h-input" id="pShortener" style="width:auto">
            <option value="none" selected>None — send raw links (recommended)</option>
            <option value="branded">Branded — your domain</option>
            <option value="signalhouse">SignalHouse sihou.io (not recommended: carrier spam blocks)</option>
          </select>
          <span class="mod-sub" id="pShortenerNote" style="margin:0;font-size:12px"></span>
        </div>
        <div style="margin-top:10px">
          <div style="font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;color:var(--muted);margin-bottom:6px">Personalization tokens <span id="pTokCount" style="color:var(--line)"></span></div>
          <div id="pTokens" style="display:flex;flex-wrap:wrap;gap:7px"><span class="mod-sub" style="margin:0;font-size:13px">Upload a file to see clickable {{tokens}}.</span></div>
        </div>
        <div style="margin-top:14px;background:linear-gradient(135deg,var(--pink),var(--pink-deep));color:#fff;border-radius:14px;padding:12px 15px;font-size:14px" id="pPreview">Your personalized message will preview here.</div>
        <div class="toolbar" style="margin:6px 0 0">
          <span class="mod-sub" id="pSeg" style="margin:0">0 characters · 0 segments</span>
          <span class="grow"></span>
          <button class="btn ghost" id="pPrevRow" style="padding:4px 10px" disabled>‹ Prev</button>
          <button class="btn ghost" id="pNextRow" style="padding:4px 10px" disabled>Next ›</button>
          <span class="mod-sub" id="pRowNav" style="margin:0"></span>
        </div>

        <div style="margin-top:16px;border-top:1px solid var(--line-soft);padding-top:14px">
          <label style="font-size:12px;font-weight:700;color:var(--muted)">Send a test to one number</label>
          <div class="toolbar" style="margin:6px 0 0">
            <input class="h-input grow" id="pTestNum" placeholder="e.g. 5618015625" />
            <button class="btn ghost" id="pTestBtn">Send test</button>
          </div>
          <div class="mod-sub" style="margin:4px 0 0;font-size:12px">Sends the current message (using the previewed row's data) to just this number. Not saved as a campaign. (Bypasses quiet-hours; still honors opt-outs.)</div>
          <div class="msg" id="pTestMsg"></div>
        </div>
      </div>

      <!-- 5) REVIEW & SEND -->
      <div class="card pad">
        <div class="mod-h">5 · Review &amp; send</div>
        <div class="mod-sub">Every recipient clears the compliance gate (suppression · consent · quiet-hours) first.</div>
        <!-- Resume banner: shown when an earlier blast was interrupted (refresh/crash/stop). -->
        <div id="pResumeBanner" style="display:none;margin:12px 0 4px;padding:12px 14px;border:1px solid var(--pink);border-radius:12px;background:rgba(253,77,150,.08)">
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
            <div style="flex:1 1 240px;font-size:13px;line-height:1.5" id="pResumeText"></div>
            <button class="btn" id="pResumeBtn">Resume send</button>
            <button class="btn ghost" id="pDiscardBtn">Discard</button>
          </div>
        </div>
        <div class="form-grid" style="margin:6px 0 12px">
          <div class="field">
            <label style="font-size:12px;font-weight:700;color:var(--muted)">Delay between messages (ms)</label>
            <input class="h-input" id="pDelay" type="number" value="250" min="0" step="50" />
          </div>
          <div class="field">
            <label style="font-size:12px;font-weight:700;color:var(--muted)">Stop after (daily cap)</label>
            <input class="h-input" id="pCap" type="number" min="0" step="500" placeholder="blank = no cap" />
          </div>
          <div class="field">
            <label style="font-size:12px;font-weight:700;color:var(--muted)">Skip T-Mobile</label>
            <label style="display:flex;align-items:center;gap:7px;font-size:13px;cursor:pointer;margin-top:8px"><input type="checkbox" id="pSkipTmo" disabled /> Exclude T-Mobile numbers</label>
            <div class="mod-sub" id="pSkipTmoHint" style="margin:4px 0 0;font-size:12px">Needs a carrier column in your file (T-Mobile / Metro / Mint).</div>
          </div>
        </div>
        <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;margin:2px 0 14px;flex-wrap:wrap">
          <input type="checkbox" id="pRequireId" checked />
          <span style="font-weight:700">Require email / CIO ID for CIO matching</span>
          <span class="mod-sub" id="pRequireIdHint" style="margin:0;font-size:12px">On — rows without an email/CIO id are excluded so every send is matchable in Customer.io.</span>
        </label>
        <div id="pAttestBox" style="border:1px solid var(--line);border-radius:10px;padding:10px 12px;margin:0 0 14px">
          <label style="display:flex;align-items:flex-start;gap:8px;font-size:13px;cursor:pointer">
            <input type="checkbox" id="pAttest" style="margin-top:2px" />
            <span><span style="font-weight:700">This list has been approved for SMS marketing</span>: everyone on it has opted in to receive text messages from Giggle Finance.</span>
          </label>
          <input class="h-input" id="pAttestSource" style="margin-top:8px" maxlength="500"
            placeholder="Where did this list / opt-in come from? (e.g. Flash promo SMS opt-ins, Sept 24)" />
          <div class="mod-sub" style="margin:6px 0 0;font-size:12px">Required for marketing sends. Recorded with your name, the time, the file and the count. Opt-outs, DNC, quiet hours and the daily limit still apply.</div>
        </div>
        <div class="toolbar">
          <button class="btn ghost" id="pPreviewBtn">Preview &amp; compliance check</button>
          <button class="btn" id="pSendBtn">Send</button>
          <button class="btn ghost" id="pPauseBtn" style="display:none">Pause</button>
          <button class="btn ghost" id="pStopBtn" style="display:none">Stop</button>
          <span class="grow"></span>
          <button class="btn ghost" id="pExportBtn" disabled>Export results CSV</button>
        </div>
        <div class="stat-row" id="pProgress" style="display:none;margin-top:14px">
          <div class="stat"><div class="k">Sent</div><div class="v" id="pgSent" style="color:var(--ok)">0</div></div>
          <div class="stat"><div class="k">Failed</div><div class="v" id="pgFail" style="color:var(--bad)">0</div></div>
          <div class="stat"><div class="k">Skipped</div><div class="v" id="pgSkip">0</div></div>
          <div class="stat"><div class="k">Remaining</div><div class="v" id="pgLeft">0</div></div>
        </div>
        <div id="pSendLogWrap" style="display:none;margin-top:14px">
          <div class="toolbar" style="margin-bottom:6px">
            <span class="mod-sub" style="font-weight:800;text-transform:uppercase;letter-spacing:.4px;font-size:11px;margin:0">Sending log</span>
            <span class="pill" id="pSendState" style="display:none">Paused</span>
          </div>
          <div class="table-wrap" style="max-height:300px;overflow:auto">
            <table class="h-table">
              <thead><tr><th>To</th><th>Status</th><th>Detail</th></tr></thead>
              <tbody id="pSendLogRows"></tbody>
            </table>
          </div>
        </div>
        <div id="pResult"></div>
        <div class="mod-sub" style="margin-top:10px;font-size:12px">Sends via SignalHouse — one personalized request per recipient. Keep this tab open while a bulk send runs. Recipients who replied STOP are already suppressed.</div>
      </div>

      <!-- 6) SCHEDULE FOR LATER -->
      <div class="card pad">
        <div class="mod-h">6 · Schedule for later (Eastern time)</div>
        <div class="mod-sub">Queue this exact send — same numbers, campaign, message and recipients — to go out automatically at a future time. The time you pick is <b>America/New_York (ET)</b>. A server-side cron fires it in batches; you don't need to keep this tab open.</div>
        <div class="toolbar" style="margin:6px 0 0">
          <input class="h-input" id="pSchedAt" type="datetime-local" style="max-width:260px" />
          <button class="btn" id="pSchedBtn">Schedule send</button>
        </div>
        <div class="msg" id="pSchedMsg"></div>
        <div style="margin-top:16px;border-top:1px solid var(--line-soft);padding-top:14px">
          <div style="font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;color:var(--muted);margin-bottom:6px">Upcoming scheduled sends</div>
          <div id="pSchedList"><div class="empty">Loading…</div></div>
        </div>
      </div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)
  let numbers = []          // [{phone,label}]
  let useCase = 'marketing' // 'marketing' | 'account_notification' — the send's use case

  // File-driven recipients (SheetJS parse). Empty ⇒ fall back to the textarea.
  let headers = []          // column names from the uploaded file
  let fileRows = []         // [{ phone (raw), vars:{Header:val} }]
  let phoneCol = null       // chosen phone-number column
  let carrierCol = null     // detected carrier column (enables Skip T-Mobile)
  let identityCol = null    // chosen email / CIO-id column (REQUIRED for CIO matching)
  let previewIdx = 0        // Prev/Next cursor into the VALID list
  let sending = false, stopRequested = false, paused = false
  let lastResults = []      // [{phone,status,detail}] for Export results CSV

  const useCaseLabel = () => (useCase === 'account_notification' ? 'Account Notification' : 'Marketing')
  // Numbers eligible for the chosen use case: those registered for it, PLUS
  // toll-free (which can carry either). A number registered for multiple use
  // cases naturally appears under each because its label would match.
  const eligibleNumbers = () => numbers.filter((n) => n.label === useCaseLabel() || n.label === 'Toll-Free')

  api.get('/api/numbers').then((r) => { numbers = r.numbers || []; renderNumbers() })
    .catch((e) => { $('pNumGroups').innerHTML = `<div class="msg bad">${esc(e.message)}</div>` })

  // ---- Wallet ----
  async function loadWallet() {
    const w = $('pWallet')
    w.textContent = 'Wallet balance …'
    try {
      const r = await api.get('/api/wallet')
      const bal = Number(r.balance || 0)
      w.textContent = `Wallet balance $${bal.toFixed(2)} · ≈${Number(r.estMessages || 0).toLocaleString()} msgs of credit`
    } catch (e) { w.textContent = 'Wallet balance — (unavailable)' }
  }
  $('pWalletRefresh').addEventListener('click', (e) => { e.preventDefault(); loadWallet() })
  loadWallet()

  // Toggle the use case (segmented buttons); clears the number selection.
  function setUseCase(uc) {
    useCase = uc
    $('pUcMarketing').className = uc === 'marketing' ? 'btn' : 'btn ghost'
    $('pUcNotif').className = uc === 'account_notification' ? 'btn' : 'btn ghost'
    renderNumbers()
    onNumChange()
  }
  $('pUcMarketing').addEventListener('click', () => setUseCase('marketing'))
  $('pUcNotif').addEventListener('click', () => setUseCase('account_notification'))

  function renderNumbers() {
    const own = eligibleNumbers().filter((n) => n.label === useCaseLabel())
    const tollFree = eligibleNumbers().filter((n) => n.label === 'Toll-Free')
    const groupHtml = (title, list) => list.length ? `
      <div style="margin-bottom:12px">
        <div style="font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;color:var(--muted);margin-bottom:6px">${esc(title)} <span style="color:var(--line)">·</span> ${list.length}</div>
        <div style="display:flex;flex-wrap:wrap;gap:7px">
          ${list.map((n) => `
            <label class="numchip" style="display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);border-radius:var(--r-pill);padding:5px 12px;font-size:13px;cursor:pointer;background:var(--surface-2)">
              <input type="checkbox" class="numck" value="${esc(n.phone)}" /> ${esc(fmtPhone(n.phone))}
            </label>`).join('')}
        </div>
      </div>` : ''
    const html = groupHtml(useCaseLabel(), own) + groupHtml('Toll-Free (either use case)', tollFree)
    $('pNumGroups').innerHTML = html || '<div class="empty">No numbers registered for this use case.</div>'
    el.querySelectorAll('.numck').forEach((ck) => ck.addEventListener('change', onNumChange))
  }

  function selected() { return [...el.querySelectorAll('.numck:checked')] }
  function poolPhones() { return selected().map((c) => c.value) }
  function derivedUseCase() { return useCase }

  function onNumChange() {
    el.querySelectorAll('.numck').forEach((ck) => {
      ck.closest('.numchip').style.borderColor = ck.checked ? 'var(--turq)' : 'var(--line)'
      ck.closest('.numchip').style.background = ck.checked ? 'color-mix(in srgb, var(--turq) 12%, transparent)' : 'var(--surface-2)'
    })
    const n = selected().length
    const badge = $('pUseBadge')
    if (!n) { badge.textContent = `${useCaseLabel()} — no numbers selected`; badge.className = 'pill' }
    else {
      badge.textContent = `${useCaseLabel()} · ${n} number${n === 1 ? '' : 's'}${n > 1 ? ' (pool)' : ''}`
      badge.className = useCase === 'account_notification' ? 'pill an' : 'pill mk'
    }
    // Opt-out enforcement is per-number and server-side, but a pool change can
    // flip which rows are duplicates/valid, so keep the preview honest.
    updatePreview()
  }
  $('pClearNums').addEventListener('click', () => {
    el.querySelectorAll('.numck').forEach((ck) => { ck.checked = false }); onNumChange()
  })
  setUseCase('marketing')

  // ---- Recipients: textarea OR uploaded file (CSV/XLSX), unified into rows ----
  // One recipient per line, "phone, email" (comma / tab / pipe separated — NOT
  // space, so a formatted "1 888 555 1234" stays intact). The phone is the first
  // token; the identity is the first email-looking token, else the second — so
  // "18885551234, jane@example.com" and "18885551234, cio_abc123" both work.
  // Identity feeds Customer.io matching and is required to send.
  function textareaRows() {
    return $('pRecipients').value.split('\n').map((s) => s.trim()).filter(Boolean).map((line) => {
      const parts = line.split(/[,\t|]/).map((s) => s.trim()).filter(Boolean)
      const phone = parts[0] || ''
      const identity = parts.slice(1).find(looksLikeEmail) || parts[1] || ''
      return { phone, identity, vars: {} }
    })
  }
  function sourceRows() { return fileRows.length ? fileRows : textareaRows() }

  // Normalize + dedupe + apply Skip-T-Mobile; annotate every row with validity.
  function prepareList() {
    const skipTmo = $('pSkipTmo').checked && !!carrierCol
    const requireId = $('pRequireId') ? $('pRequireId').checked : true
    const seen = new Set()
    const out = []
    for (const r of sourceRows()) {
      const norm = normPhone(r.phone)
      const identity = String(r.identity != null ? r.identity : '').trim()
      if (norm.length !== 11) { out.push({ ...r, norm, identity, valid: false, reason: 'invalid' }); continue }
      if (seen.has(norm)) { out.push({ ...r, norm, identity, valid: false, reason: 'duplicate' }); continue }
      if (skipTmo && isTmobile(r.vars[carrierCol])) { seen.add(norm); out.push({ ...r, norm, identity, valid: false, reason: 'tmobile' }); continue }
      // No email / CIO id: exclude it when matching is required; otherwise send it
      // anyway (it just won't be attributed in Customer.io) and flag it unmatched.
      if (!identity) {
        seen.add(norm)
        if (requireId) { out.push({ ...r, norm, identity, valid: false, reason: 'no_id' }) }
        else { out.push({ ...r, norm, identity, valid: true, unmatched: true }) }
        continue
      }
      seen.add(norm); out.push({ ...r, norm, identity, valid: true })
    }
    return out
  }
  function validList() { return prepareList().filter((r) => r.valid) }

  function updateCount() {
    const prepared = prepareList()
    const valid = prepared.filter((r) => r.valid).length
    const invalid = prepared.filter((r) => r.reason === 'invalid').length
    const dupe = prepared.filter((r) => r.reason === 'duplicate').length
    const tmo = prepared.filter((r) => r.reason === 'tmobile').length
    const noId = prepared.filter((r) => r.reason === 'no_id').length     // excluded (require ON)
    const unmatched = prepared.filter((r) => r.unmatched).length         // sending unmatched (require OFF)
    const suffix = noId ? ` · ${noId} missing ID (excluded)` : unmatched ? ` · ${unmatched} unmatched` : ''
    $('pCount').textContent = `${prepared.length} row${prepared.length === 1 ? '' : 's'}${prepared.length ? ` · ${valid} valid${suffix}` : ''}`
    if (fileRows.length) {
      $('pStatRows').textContent = prepared.length
      $('pStatValid').textContent = valid
      $('pStatInvalid').textContent = invalid
      $('pStatDupe').textContent = dupe
      $('pStatNoId').textContent = noId + unmatched
      $('pStatTmo').textContent = tmo
    }
  }
  $('pShortener').addEventListener('change', () => {
    const v = $('pShortener').value
    $('pShortenerNote').innerHTML = v === 'branded'
      ? '<span style="color:var(--warn)">Needs a short domain configured — falls back to raw links until set.</span>'
      : v === 'none' ? 'Links sent exactly as written. Best deliverability.'
      : '<span style="color:var(--bad)">sihou.io links are carrier spam-blocked (~30% fail). Use only for testing.</span>'
  })
  $('pRecipients').addEventListener('input', () => { updateCount(); previewIdx = 0; updatePreview() })
  $('pRequireId').addEventListener('change', () => {
    $('pRequireIdHint').textContent = $('pRequireId').checked
      ? 'On — rows without an email/CIO id are excluded so every send is matchable in Customer.io.'
      : 'Off — phone-only rows still send; they just won’t be matched in Customer.io.'
    updateCount(); previewIdx = 0; updatePreview()
  })

  // ---- File upload (CSV + XLSX/XLS via SheetJS, one code path) ----
  function ingestMatrix(matrix, fileName) {
    if (!matrix || !matrix.length) { fileError('That file has no rows.'); return }
    const rawCols = matrix[0].map((h) => String(h == null ? '' : h).trim())
    headers = rawCols.filter((h) => h !== '')
    const dataRows = matrix.slice(1)
      .filter((arr) => arr.some((v) => String(v == null ? '' : v).trim() !== ''))
      .map((arr) => { const o = {}; rawCols.forEach((c, i) => { if (c) o[c] = arr[i] == null ? '' : arr[i] }); return o })
    if (!headers.length || !dataRows.length) { fileError('Couldn\'t find headers + rows in that file.'); return }
    phoneCol = guessPhoneCol(headers)
    carrierCol = guessCarrierCol(headers)
    identityCol = guessIdentityCol(headers)
    fileRows = dataRows.map((row) => ({ phone: row[phoneCol], identity: identityCol ? row[identityCol] : '', vars: row }))
    // Phone-column selector.
    const sel = $('pPhoneCol')
    sel.innerHTML = headers.map((h) => `<option value="${esc(h)}"${h === phoneCol ? ' selected' : ''}>${esc(h)}</option>`).join('')
    // Identity (email / CIO id) column selector — REQUIRED. A leading blank option
    // makes "none chosen" explicit so the operator sees they must pick one.
    const idSel = $('pIdCol')
    idSel.innerHTML = `<option value="">— none —</option>` +
      headers.map((h) => `<option value="${esc(h)}"${h === identityCol ? ' selected' : ''}>${esc(h)}</option>`).join('')
    $('pFileName').textContent = fileName
    $('pFileInfo').style.display = 'block'
    updateSkipTmoHint()
    buildTokens()
    previewIdx = 0
    updateCount(); updatePreview()
  }
  function fileError(m) { $('pCount').textContent = m }
  function rebuildFromCols() {
    // Re-map fileRows' phone + identity fields when the operator picks columns.
    phoneCol = $('pPhoneCol').value
    identityCol = $('pIdCol').value || null
    fileRows = fileRows.map((r) => ({ phone: r.vars[phoneCol], identity: identityCol ? r.vars[identityCol] : '', vars: r.vars }))
    previewIdx = 0; updateCount(); updatePreview()
  }
  $('pPhoneCol').addEventListener('change', rebuildFromCols)
  $('pIdCol').addEventListener('change', rebuildFromCols)

  function clearFile() {
    headers = []; fileRows = []; phoneCol = null; carrierCol = null; identityCol = null
    $('pFileInfo').style.display = 'none'
    $('pFile').value = ''
    $('pSkipTmo').checked = false; $('pSkipTmo').disabled = true
    updateSkipTmoHint()
    buildTokens()
    previewIdx = 0; updateCount(); updatePreview()
  }
  $('pClearFile').addEventListener('click', clearFile)

  $('pFile').addEventListener('change', (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        // SheetJS reads CSV, XLSX and XLS from an array buffer (it sniffs the type).
        const wb = XLSX.read(new Uint8Array(reader.result), { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        if (!ws) { fileError('That workbook has no sheets.'); return }
        const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' })
        ingestMatrix(matrix, file.name)
      } catch (err) { fileError('Couldn\'t read that file: ' + (err && err.message)) }
    }
    reader.readAsArrayBuffer(file)
  })

  function updateSkipTmoHint() {
    const box = $('pSkipTmo'), hint = $('pSkipTmoHint')
    if (carrierCol) {
      box.disabled = false
      hint.textContent = box.checked
        ? `Using carrier column "${carrierCol}" — T-Mobile / Metro / Mint rows are excluded.`
        : `Carrier column "${carrierCol}" detected — check to exclude T-Mobile / Metro / Mint rows.`
    } else {
      box.disabled = true; box.checked = false
      hint.textContent = 'Needs a carrier column in your file (T-Mobile / Metro / Mint). None detected.'
    }
  }
  $('pSkipTmo').addEventListener('change', () => { updateSkipTmoHint(); updateCount(); previewIdx = 0; updatePreview() })

  // ---- Personalization token chips ----
  function buildTokens() {
    const box = $('pTokens'), cnt = $('pTokCount')
    if (!headers.length) {
      box.innerHTML = '<span class="mod-sub" style="margin:0;font-size:13px">Upload a file to see clickable {{tokens}}.</span>'
      cnt.textContent = ''
      return
    }
    cnt.textContent = `· ${headers.length} available`
    box.innerHTML = headers.map((h) =>
      `<span class="pill" data-tok="${esc(h)}" style="cursor:pointer" title="Insert {{${esc(h)}}}">{{${esc(h)}}}</span>`).join('')
    box.querySelectorAll('[data-tok]').forEach((chip) =>
      chip.addEventListener('click', () => insertToken('{{' + chip.getAttribute('data-tok') + '}}')))
  }
  function insertToken(tok) {
    const ta = $('pBody')
    const s = ta.selectionStart ?? ta.value.length
    const e = ta.selectionEnd ?? ta.value.length
    ta.value = ta.value.slice(0, s) + tok + ta.value.slice(e)
    ta.focus()
    ta.selectionStart = ta.selectionEnd = s + tok.length
    updatePreview()
  }

  // ---- Message preview + Prev/Next paging ----
  function updatePreview() {
    const list = validList()
    const tpl = $('pBody').value
    if (!list.length) {
      const sample = personalizePreview(tpl, { first_name: 'Alex' })
      $('pPreview').textContent = sample || 'Your personalized message will preview here.'
      const len = tpl.length, seg = segments(tpl)
      $('pSeg').textContent = `${len} character${len === 1 ? '' : 's'} · ${seg} segment${seg === 1 ? '' : 's'}`
      $('pRowNav').textContent = ''
      $('pPrevRow').disabled = $('pNextRow').disabled = true
      return
    }
    if (previewIdx >= list.length) previewIdx = 0
    if (previewIdx < 0) previewIdx = 0
    const row = list[previewIdx]
    const text = personalizePreview(tpl, row.vars)
    $('pPreview').textContent = text || '(empty message)'
    const len = text.length, seg = segments(text)
    $('pSeg').textContent = `${len} character${len === 1 ? '' : 's'} · ${seg} segment${seg === 1 ? '' : 's'}`
    $('pRowNav').textContent = `${previewIdx + 1} of ${list.length} · to ${fmtPhone(row.norm)}`
    $('pPrevRow').disabled = previewIdx <= 0
    $('pNextRow').disabled = previewIdx >= list.length - 1
  }
  $('pBody').addEventListener('input', updatePreview)
  $('pPrevRow').addEventListener('click', () => { if (previewIdx > 0) { previewIdx--; updatePreview() } })
  $('pNextRow').addEventListener('click', () => { previewIdx++; updatePreview() })

  // ---- Test send (unchanged behaviour; uses the previewed row's data) ----
  $('pTestBtn').addEventListener('click', async () => {
    const msg = $('pTestMsg'); msg.className = 'msg'; msg.textContent = ''
    const from = poolPhones()[0], to = $('pTestNum').value.trim(), body = $('pBody').value.trim()
    if (!from) { msg.className = 'msg bad'; msg.textContent = 'Select a From number above.'; return }
    if (!to) { msg.className = 'msg bad'; msg.textContent = 'Enter a test number.'; return }
    if (!body) { msg.className = 'msg bad'; msg.textContent = 'Write a message first.'; return }
    const list = validList()
    const vars = list.length ? list[Math.min(previewIdx, list.length - 1)].vars : { first_name: 'Alex' }
    msg.textContent = 'Sending test…'
    try {
      const r = await api.post('/api/send', { test: true, to, from, useCase: derivedUseCase(), body, vars, shortener: $('pShortener').value })
      if (r.sent) { msg.className = 'msg ok'; msg.textContent = `Test sent to ${fmtPhone(to)}.` }
      else if (r.blocked) { msg.className = 'msg bad'; msg.textContent = 'Blocked: ' + r.blocked.join(', ') }
      else { msg.className = 'msg bad'; msg.textContent = 'Failed: ' + (r.error || 'unknown') }
    } catch (e) { msg.className = 'msg bad'; msg.textContent = e.message }
  })

  // ---- List SMS approval (marketing sends only) ----
  // Any uploaded list may be texted for marketing once the operator confirms it
  // is approved for SMS. The confirmation is recorded server-side (who, when,
  // file, source, count) and its id rides along with the send or schedule.
  function approvalNeeded() { return derivedUseCase() === 'marketing' }
  function approvalTicked() { return !!($('pAttest') && $('pAttest').checked) }
  async function recordApproval(campaignName, count) {
    if (!approvalNeeded()) return null
    if (!approvalTicked()) throw new Error('Tick "This list has been approved for SMS marketing" before sending a marketing list.')
    const r = await api.post('/api/attestation', {
      confirmed: true, campaignName, recipientCount: count,
      listName: ($('pFileName') && $('pFileName').textContent) || '',
      sourceNote: $('pAttestSource').value.trim(),
    })
    return r.id
  }

  // ---- Batch dry-run: "Preview & compliance check" (unchanged /api/send path) ----
  function renderResult(r) {
    const rows = (r.blocked || []).map((b) => `<tr><td>${esc(fmtPhone(b.phone))}</td><td>${esc((b.reasons || []).join(', '))}</td></tr>`).join('')
    $('pResult').innerHTML = `
      <div class="stat-row" style="margin-top:14px">
        <div class="stat"><div class="k">Total</div><div class="v">${r.total || 0}</div></div>
        <div class="stat"><div class="k">Sendable</div><div class="v" style="color:var(--ok)">${r.sendable || 0}</div></div>
        <div class="stat"><div class="k">Blocked</div><div class="v" style="color:var(--bad)">${(r.blocked || []).length}</div></div>
      </div>
      ${rows ? `<div class="table-wrap" style="margin-top:12px"><table class="h-table"><thead><tr><th>Blocked recipient</th><th>Reasons</th></tr></thead><tbody>${rows}</tbody></table></div>` : ''}
      ${policyNote(r)}
      <div class="msg ok" style="margin-top:8px">Preview only — nothing sent.</div>`
  }
  // Account policy context for the preview: daily marketing limit + opt-in coverage.
  function policyNote(r) {
    const p = r.policy
    if (!p) return ''
    const capped = (r.blocked || []).filter((b) => (b.reasons || []).includes('daily_cap')).length
    const noOptIn = (r.warnings && r.warnings.noOptIn) || 0
    const lines = []
    if (capped) lines.push(`${capped.toLocaleString()} held back by the account limit of ${p.marketingDailyCap.toLocaleString()} marketing texts per day.`)
    if (r.listApproved) lines.push('List marked as approved for SMS: opt-in check satisfied. Your confirmation is recorded when you send or schedule.')
    if (noOptIn) lines.push(`${noOptIn.toLocaleString()} sendable recipient${noOptIn === 1 ? ' has' : 's have'} no SMS opt-in on file (opt-in check is set to "${p.optInMode}").`)
    return lines.length ? `<div class="msg" style="margin-top:8px">${lines.map(esc).join('<br>')}</div>` : ''
  }
  async function runPreview() {
    const campaignName = $('pCampaign').value.trim()
    const pool = poolPhones()
    const list = validList()
    if (!pool.length) { $('pResult').innerHTML = '<div class="msg bad">Select at least one From number.</div>'; return }
    if (!campaignName) { $('pResult').innerHTML = '<div class="msg bad">Campaign name required.</div>'; return }
    if (!$('pBody').value.trim()) { $('pResult').innerHTML = '<div class="msg bad">Write a message.</div>'; return }
    if (!list.length) { $('pResult').innerHTML = '<div class="msg bad">Add at least one recipient.</div>'; return }
    $('pResult').innerHTML = '<div class="empty">Working…</div>'
    const payload = {
      campaignName, from: pool[0], useCase: derivedUseCase(),
      recipients: list.map((r) => ({ phone: r.norm, identity: r.identity, vars: r.vars })),
      body: $('pBody').value.trim(), dryRun: true, shortener: $('pShortener').value,
      // Preview simulates the approval without recording one (nothing is sent).
      previewListApproved: approvalNeeded() && approvalTicked(),
    }
    if (pool.length > 1) payload.pool = pool
    try { renderResult(await api.post('/api/send', payload)) }
    catch (e) { $('pResult').innerHTML = `<div class="msg bad">${esc(e.message)}</div>` }
  }
  $('pPreviewBtn').addEventListener('click', runPreview)

  // ---- Real send: CLIENT-DRIVEN LOOP over /api/send-one ----
  function setSending(on) {
    sending = on
    if (on) paused = false
    $('pSendBtn').style.display = on ? 'none' : 'inline-block'
    $('pStopBtn').style.display = on ? 'inline-block' : 'none'
    $('pPauseBtn').style.display = on ? 'inline-block' : 'none'
    $('pPauseBtn').textContent = 'Pause'
    $('pSendState').style.display = 'none'
    $('pPreviewBtn').disabled = on
    $('pProgress').style.display = on || lastResults.length ? 'flex' : 'none'
  }
  $('pStopBtn').addEventListener('click', () => { stopRequested = true; paused = false; $('pStopBtn').textContent = 'Stopping…' })
  // Pause holds the loop between sends (no request is abandoned mid-flight); Resume continues.
  $('pPauseBtn').addEventListener('click', () => {
    paused = !paused
    $('pPauseBtn').textContent = paused ? 'Resume' : 'Pause'
    $('pSendState').style.display = paused ? 'inline-block' : 'none'
  })

  // Append a live log row (newest on top), keeping the DOM bounded for big sends.
  function logSendRow(phone, status, detail) {
    const tb = $('pSendLogRows'); if (!tb) return
    const pill = status === 'sent' ? 'an' : status === 'skipped' ? '' : 'mk'
    tb.insertAdjacentHTML('afterbegin',
      `<tr><td>${esc(fmtPhone(phone))}</td><td><span class="pill ${pill}">${esc(status)}</span></td>` +
      `<td style="max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--text-2)">${esc(detail || '')}</td></tr>`)
    while (tb.children.length > 250) tb.removeChild(tb.lastChild)
  }

  // Build the send job from the form, persist an initial checkpoint, then drive it.
  async function runSend() {
    const campaignName = $('pCampaign').value.trim()
    const pool = poolPhones()
    const body = $('pBody').value.trim()
    const list = validList()
    if (!pool.length) { $('pResult').innerHTML = '<div class="msg bad">Select at least one From number.</div>'; return }
    if (!campaignName) { $('pResult').innerHTML = '<div class="msg bad">Campaign name required.</div>'; return }
    if (!body) { $('pResult').innerHTML = '<div class="msg bad">Write a message.</div>'; return }
    if (!list.length) { $('pResult').innerHTML = '<div class="msg bad">Add at least one recipient.</div>'; return }
    if (approvalNeeded() && !approvalTicked()) {
      $('pResult').innerHTML = '<div class="msg bad">Tick “This list has been approved for SMS marketing” before sending a marketing list.</div>'; return
    }

    const delay = Math.max(0, parseInt($('pDelay').value || '0', 10))
    const cap = parseInt($('pCap').value || '0', 10) || 0
    if (!confirm(`Send to ${list.length} recipient${list.length === 1 ? '' : 's'} from ${pool.length > 1 ? pool.length + ' pooled ' + useCaseLabel() + ' numbers' : fmtPhone(pool[0])}?`)) return

    let attestationId = null
    try { attestationId = await recordApproval(campaignName, list.length) }
    catch (e) { $('pResult').innerHTML = `<div class="msg bad">${esc(e.message)}</div>`; return }

    // The job is fully self-describing so it can be resumed WITHOUT the form/file
    // still being loaded — store only what the loop needs per recipient.
    const job = {
      jobId: campaignName + ':' + Date.now(),
      campaignName, pool, useCase: derivedUseCase(), body, shortener: $('pShortener').value,
      delay, cap, total: list.length, startedAt: Date.now(), attestationId,
      list: list.map((r) => ({ norm: r.norm, identity: r.identity, vars: r.vars })),
      cursor: 0, sent: 0, failed: 0, skipped: 0,
    }
    await saveCheckpoint({ ...job, savedAt: Date.now() })
    await driveSend(job, { resume: false })
  }

  // The actual send loop — parameterized by a job + start cursor so BOTH a fresh
  // send and a resume run through the same code. Checkpoints after every message
  // so an interruption resumes from the exact next-unsent row.
  async function driveSend(job, { resume } = {}) {
    const { campaignName, pool, body } = job
    lastResults = []
    stopRequested = false
    setSending(true)
    hideResumeBanner()
    let sent = job.sent || 0, failed = job.failed || 0, skipped = job.skipped || 0
    $('pgSent').textContent = sent; $('pgFail').textContent = failed; $('pgSkip').textContent = skipped
    $('pgLeft').textContent = job.total - job.cursor
    $('pResult').innerHTML = resume
      ? `<div class="msg" style="margin-top:12px">Resuming “${esc(campaignName)}” at ${Number(job.cursor).toLocaleString()} of ${Number(job.total).toLocaleString()}…</div>`
      : ''
    $('pExportBtn').disabled = true
    $('pSendLogRows').innerHTML = ''
    $('pSendLogWrap').style.display = 'block'

    const { delay, cap } = job
    let i = job.cursor
    let capHit = false
    let accountCapHit = false
    for (; i < job.list.length; i++) {
      if (stopRequested) break
      // Pause holds here between messages — no in-flight request is dropped.
      while (paused && !stopRequested) await sleep(200)
      if (stopRequested) break
      const row = job.list[i]
      const from = pool[i % pool.length] // round-robin across the pool
      let r
      try {
        r = await api.post('/api/send-one', {
          campaignName, from, useCase: job.useCase, phone: row.norm, identity: row.identity, vars: row.vars, body, shortener: job.shortener,
          attestationId: job.attestationId || null,
        })
      } catch (e) { r = { sent: false, error: e.message } }

      // Account-wide daily marketing cap reached on the server: stop WITHOUT
      // consuming this row, so a resume (tomorrow) starts right here.
      if (r.blocked && r.blocked.includes('daily_cap')) { accountCapHit = r.cap || true; break }

      let st, detail
      if (r.sent) { sent++; st = 'sent'; detail = r.id || '' }
      else if (r.blocked) { skipped++; st = 'skipped'; detail = (r.blocked || []).join('; ') }
      else { failed++; st = 'failed'; detail = r.error || 'error' }
      lastResults.push({ phone: row.norm, status: st, detail })
      logSendRow(row.norm, st, detail)

      $('pgSent').textContent = sent; $('pgFail').textContent = failed; $('pgSkip').textContent = skipped
      $('pgLeft').textContent = job.total - (i + 1)

      // Checkpoint AFTER the send lands so a refresh resumes at the NEXT row.
      await saveCheckpoint({ ...job, cursor: i + 1, sent, failed, skipped, savedAt: Date.now() })

      if (cap && sent >= cap) { capHit = true; i++; break } // daily cap: pause the run
      if (delay && i + 1 < job.list.length && !stopRequested) await sleep(delay)
    }

    const finished = i >= job.list.length
    // Clear the checkpoint only on a natural finish; a stop/cap keeps it so the
    // operator can resume (the last per-message save already holds cursor = i).
    if (finished) await clearCheckpoint()
    else await saveCheckpoint({ ...job, cursor: i, sent, failed, skipped, savedAt: Date.now() })

    setSending(false)
    $('pExportBtn').disabled = lastResults.length === 0
    const remaining = job.list.length - i
    const note = stopRequested
      ? `Stopped at ${Number(i).toLocaleString()} of ${Number(job.total).toLocaleString()}. ${sent} sent, ${failed} failed, ${skipped} skipped, ${remaining} not attempted — resume anytime.`
      : accountCapHit
        ? `Account daily limit${accountCapHit === true ? '' : ' of ' + Number(accountCapHit).toLocaleString()} marketing texts reached (${sent} sent this run). ${remaining} not attempted — resume tomorrow.`
        : capHit
          ? `Daily cap of ${cap} reached (${sent} sent). ${remaining} not attempted — resume anytime.`
          : `Done. ${sent} sent, ${failed} failed, ${skipped} skipped.`
    $('pStopBtn').textContent = 'Stop'
    $('pResult').innerHTML = `<div class="msg ${failed ? 'bad' : finished ? 'ok' : ''}" style="margin-top:12px">${esc(note)}</div>`
    if (!finished) checkForResume() // re-surface the banner so Resume is one click away
    loadWallet()
  }
  $('pSendBtn').addEventListener('click', runSend)

  // ---- Resume banner (checkpoint recovery) ----
  let pendingCheckpoint = null
  function fmtAgo(ts) {
    const s = Math.max(0, Math.round((Date.now() - (ts || 0)) / 1000))
    if (s < 60) return s + 's ago'
    const m = Math.round(s / 60); if (m < 60) return m + 'm ago'
    const h = Math.round(m / 60); if (h < 24) return h + 'h ago'
    return Math.round(h / 24) + 'd ago'
  }
  function hideResumeBanner() { const b = $('pResumeBanner'); if (b) b.style.display = 'none' }
  function showResumeBanner(cp) {
    pendingCheckpoint = cp
    const b = $('pResumeBanner'); if (!b) return
    $('pResumeText').innerHTML =
      `<b>Unfinished send</b> — “${esc(cp.campaignName)}”: <b>${Number(cp.cursor).toLocaleString()}</b> of ` +
      `<b>${Number(cp.total).toLocaleString()}</b> done ` +
      `<span style="color:var(--muted)">· ${cp.sent || 0} sent, ${cp.failed || 0} failed, ${cp.skipped || 0} skipped · saved ${fmtAgo(cp.savedAt)}</span>`
    b.style.display = 'block'
  }
  // On mount (and after an interrupted run) surface any resumable checkpoint.
  async function checkForResume() {
    if (sending) return
    const cp = await loadCheckpoint()
    if (cp && Array.isArray(cp.list) && Number(cp.cursor) < cp.list.length) showResumeBanner(cp)
    else hideResumeBanner()
  }
  $('pResumeBtn').addEventListener('click', async () => {
    if (!pendingCheckpoint || sending) return
    await driveSend(pendingCheckpoint, { resume: true })
  })
  $('pDiscardBtn').addEventListener('click', async () => {
    if (!confirm('Discard the unfinished send? You won’t be able to resume it.')) return
    await clearCheckpoint(); pendingCheckpoint = null; hideResumeBanner()
  })

  // ---- Export results CSV ----
  $('pExportBtn').addEventListener('click', () => {
    if (!lastResults.length) return
    const escCsv = (v) => { let s = String(v == null ? '' : v); if (/^[=+\-@\t\r]/.test(s)) s = "'" + s; return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
    const lines = [['phone', 'status', 'reason_or_id'].join(',')]
      .concat(lastResults.map((r) => [r.phone, r.status, r.detail].map(escCsv).join(',')))
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'honeypacker-results-' + new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-') + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  })

  // ---- Schedule for later (ET) — lifecycle + live sending log ----------------
  const SCHED_PILL = { sending: 'an', paused: 'mk', done: 'an', pending: '', canceled: '' }
  let schedLogId = null      // campaign currently shown in the log panel
  const statusRow = (s) => {
    const pct2 = s.total ? Math.min(100, Math.round((Number(s.cursor || 0) / s.total) * 100)) : 0
    const acts = []
    if (s.status === 'pending') acts.push(['start_now', 'Start now'], ['reschedule', 'Reschedule'], ['to_back', 'Move to end'], ['cancel', 'Cancel'])
    else if (s.status === 'sending') acts.push(['pause', 'Pause'], ['cancel', 'Cancel'])
    else if (s.status === 'paused') acts.push(['resume', 'Resume'], ['reschedule', 'Reschedule'], ['to_back', 'Move to end'], ['cancel', 'Cancel'])
    return `
      <tr>
        <td>${esc(s.name || '(unnamed)')}</td>
        <td style="white-space:nowrap">${esc(fmtEt(s.send_at))}</td>
        <td style="min-width:150px">
          <div style="display:flex;align-items:center;gap:8px">
            <div style="flex:1;height:6px;background:var(--line);border-radius:3px;overflow:hidden"><div style="height:100%;width:${pct2}%;background:var(--turq)"></div></div>
            <span class="mod-sub" style="margin:0;white-space:nowrap">${Number(s.cursor || 0).toLocaleString()}/${Number(s.total || 0).toLocaleString()}</span>
          </div>
        </td>
        <td><span class="pill ${SCHED_PILL[s.status] || ''}">${esc(s.status || '')}</span></td>
        <td style="white-space:nowrap">
          ${acts.map(([a, label]) => `<button class="btn ghost" data-act="${a}" data-id="${esc(s.id)}" style="padding:4px 10px;margin-right:4px">${label}</button>`).join('')}
          <button class="btn ghost" data-log="${esc(s.id)}" style="padding:4px 10px">Log</button>
        </td>
      </tr>`
  }

  async function loadScheduled() {
    const box = $('pSchedList')
    try {
      const r = await api.get('/api/scheduled')
      const rows = r.scheduled || []
      if (!rows.length) { box.innerHTML = '<div class="empty">No scheduled campaigns.</div>'; schedLogId = null; return }
      box.innerHTML = `
        <div class="table-wrap">
          <table class="h-table">
            <thead><tr><th>Name</th><th>When (ET)</th><th>Progress</th><th>Status</th><th>Controls</th></tr></thead>
            <tbody>${rows.map(statusRow).join('')}</tbody>
          </table>
        </div>
        <div id="pSchedLog" style="margin-top:12px"></div>`

      box.querySelectorAll('[data-act]').forEach((btn) => btn.addEventListener('click', async () => {
        const action = btn.getAttribute('data-act'), id = btn.getAttribute('data-id')
        const row = rows.find((s) => s.id === id) || {}
        const payload = { id, action }
        if (action === 'cancel' && !confirm('Cancel this campaign? This cannot be undone.')) return
        if (action === 'reschedule') {
          const input = prompt(`New send time for "${row.name || 'this campaign'}" (Eastern time, YYYY-MM-DD HH:MM):`, msToEtWallClock(row.send_at))
          if (input == null) return
          const sendAt = etWallClockToMs(String(input).trim().replace(' ', 'T'))
          if (!Number.isFinite(sendAt)) { alert('Use the format YYYY-MM-DD HH:MM, e.g. 2026-10-11 11:00'); return }
          if (sendAt <= Date.now()) { alert('Pick a time in the future (ET).'); return }
          if (!confirm(`Move "${row.name || 'this campaign'}" to ${fmtEt(sendAt)}?`)) return
          payload.sendAt = sendAt
        }
        if (action === 'to_back' && !confirm(`Move "${row.name || 'this campaign'}" to the end of the queue (one day after the last scheduled ${row.use_case === 'account_notification' ? 'account-notification' : 'marketing'} send)?`)) return
        btn.disabled = true
        try {
          const r = await api.patch('/api/scheduled', payload)
          if ((action === 'reschedule' || action === 'to_back') && r.send_at) alert(`Rescheduled to ${fmtEt(r.send_at)}.`)
          loadScheduled()
        } catch (e) { btn.disabled = false; alert(e.message) }
      }))
      box.querySelectorAll('[data-log]').forEach((btn) => btn.addEventListener('click', () => {
        schedLogId = schedLogId === btn.getAttribute('data-log') ? null : btn.getAttribute('data-log')
        renderSchedLog()
      }))
      if (schedLogId) renderSchedLog()

      // Self-terminating live poll: refresh while any campaign is sending, but
      // stop once this list leaves the DOM (navigation away) — no leaked timer.
      if (rows.some((s) => s.status === 'sending') && document.body.contains(box)) {
        setTimeout(() => { if (document.body.contains(box)) loadScheduled() }, 6000)
      }
    } catch (e) { box.innerHTML = `<div class="msg bad">${esc(e.message)}</div>` }
  }

  async function renderSchedLog() {
    const panel = $('pSchedLog'); if (!panel) return
    if (!schedLogId) { panel.innerHTML = ''; return }
    try {
      const { row, log } = await api.get('/api/scheduled?id=' + encodeURIComponent(schedLogId))
      panel.innerHTML = `
        <div class="card pad" style="background:var(--surface-2)">
          <div class="toolbar" style="margin-bottom:8px">
            <div class="mod-h" style="margin:0">Sending log — ${esc(row.name || '')}</div>
            <span class="grow"></span>
            <span class="mod-sub" style="margin:0">${Number(row.cursor || 0).toLocaleString()} / ${Number(row.total || 0).toLocaleString()} · ${esc(row.status)}</span>
          </div>
          <div class="table-wrap" style="max-height:280px;overflow:auto">
            <table class="h-table">
              <thead><tr><th>Sent (ET)</th><th>To</th><th>Status</th><th>Error</th></tr></thead>
              <tbody>${log.length ? log.map((m) => `
                <tr>
                  <td style="white-space:nowrap">${m.sent_at ? esc(fmtEt(m.sent_at)) : '—'}</td>
                  <td>${esc(fmtPhone(m.to_phone))}</td>
                  <td><span class="pill ${m.status === 'delivered' ? 'an' : (m.status === 'failed' || m.status === 'undelivered') ? 'mk' : ''}">${esc(m.status || '')}</span></td>
                  <td>${esc(m.error_code || '')}</td>
                </tr>`).join('') : '<tr><td colspan="4" class="empty">No messages sent yet.</td></tr>'}
              </tbody>
            </table>
          </div>
        </div>`
      // Keep the log live while the campaign is actively sending.
      if (row.status === 'sending' && schedLogId && document.body.contains(panel)) {
        setTimeout(() => { if (schedLogId && document.body.contains(panel)) renderSchedLog() }, 4000)
      }
    } catch (e) { panel.innerHTML = `<div class="msg bad">${esc(e.message)}</div>` }
  }

  $('pSchedBtn').addEventListener('click', async () => {
    const msg = $('pSchedMsg'); msg.className = 'msg'; msg.textContent = ''
    // Reuse the SAME inputs the send loop builds its payload from.
    const campaignName = $('pCampaign').value.trim()
    const pool = poolPhones()
    const body = $('pBody').value.trim()
    const list = validList()
    if (!pool.length) { msg.className = 'msg bad'; msg.textContent = 'Select at least one From number.'; return }
    if (!campaignName) { msg.className = 'msg bad'; msg.textContent = 'Campaign name required.'; return }
    if (!body) { msg.className = 'msg bad'; msg.textContent = 'Write a message.'; return }
    if (!list.length) { msg.className = 'msg bad'; msg.textContent = 'Add at least one recipient.'; return }
    const rawAt = $('pSchedAt').value
    if (!rawAt) { msg.className = 'msg bad'; msg.textContent = 'Pick a date & time.'; return }
    const sendAt = etWallClockToMs(rawAt)
    if (!Number.isFinite(sendAt)) { msg.className = 'msg bad'; msg.textContent = 'Invalid date & time.'; return }
    if (sendAt <= Date.now()) { msg.className = 'msg bad'; msg.textContent = 'Pick a time in the future (ET).'; return }

    const payload = {
      campaignName, from: pool[0], pool, useCase: derivedUseCase(), body, shortener: $('pShortener').value,
      recipients: list.map((r) => ({ phone: r.norm, identity: r.identity, vars: r.vars })),
      sendAt,
    }
    if (approvalNeeded() && !approvalTicked()) {
      msg.className = 'msg bad'; msg.textContent = 'Tick “This list has been approved for SMS marketing” before scheduling a marketing list.'; return
    }
    if (!confirm(`Schedule ${list.length} recipient${list.length === 1 ? '' : 's'} for ${fmtEt(sendAt)}?`)) return
    msg.textContent = 'Scheduling…'
    try {
      payload.attestationId = await recordApproval(campaignName, list.length)
      const r = await api.post('/api/scheduled', payload)
      msg.className = 'msg ok'
      msg.textContent = `Scheduled ${Number(r.total || 0).toLocaleString()} recipient${r.total === 1 ? '' : 's'} for ${fmtEt(sendAt)}.` + (r.warning ? ' ' + r.warning : '')
      loadScheduled()
    } catch (e) { msg.className = 'msg bad'; msg.textContent = e.message }
  })
  loadScheduled()

  updateCount(); updatePreview()
  checkForResume() // surface any send interrupted by a refresh/crash/stop
}
